import React, {useEffect, useMemo, useRef, useState, PropsWithChildren} from 'react';
import classnames from 'classnames';
import {throttle} from 'lodash';

import CarouselArrow, {Props as CarouselArrowProps} from 'shared/components/carousel-arrow';

interface Props {
  arrowProps?: CarouselArrowProps;
  carouselKey?: number | string;
  className?: string;
  fade?: boolean;
}

// Assumes all items in the carousel are the same width
const Carousel = ({
  children,
  arrowProps,
  carouselKey = 1,
  className,
  fade,
}: Props & PropsWithChildren) => {
  const scrollBody = useRef<HTMLDivElement>(null);
  const scrollContainer = useRef<HTMLDivElement>(null);
  const interval = useRef<NodeJS.Timeout | number | undefined>(undefined);
  const [marginLeft, setMarginLeft] = useState(0);
  const [marginRight, setMarginRight] = useState(0);
  const [end, setEnd] = useState<number | null>(null);
  const [overflow, setOverflow] = useState(true);
  const isLeftArrowVisible = overflow && marginLeft < 0;
  const isRightArrowVisible = overflow && marginLeft !== end;

  const throttledCalculateOverflow = useMemo(
    () =>
      throttle(() => {
        const {firstChild, childElementCount} = scrollBody.current || {};
        const {offsetWidth: containerWidth} = scrollContainer.current || {};

        if (
          !firstChild ||
          !(firstChild instanceof HTMLElement) ||
          !firstChild.offsetWidth ||
          !containerWidth ||
          !childElementCount
        ) {
          return;
        }

        // Set overflow to false if the number of items the carousel can display
        // is greater or equal to than the number of children passed in.
        const childStyles = window.getComputedStyle(firstChild);
        const marginWidth = parseInt(childStyles.marginLeft) + parseInt(childStyles.marginRight);
        const maxCanShow = Math.floor(containerWidth / (firstChild.offsetWidth + marginWidth));

        setOverflow(maxCanShow < childElementCount);
      }, 500),
    []
  );

  useEffect(() => {
    window.addEventListener('resize', throttledCalculateOverflow);
    setTimeout(throttledCalculateOverflow);
    interval.current = setInterval(throttledCalculateOverflow, 1000);

    return () => {
      window.removeEventListener('resize', throttledCalculateOverflow);
      clearInterval(interval.current);
    };
  }, [throttledCalculateOverflow]);

  const handlePrevClick = () => {
    const {firstChild} = scrollBody.current || {};
    const {offsetWidth: containerWidth} = scrollContainer.current || {};

    if (
      !firstChild ||
      !(firstChild instanceof HTMLElement) ||
      !firstChild.offsetWidth ||
      !containerWidth
    ) {
      return;
    }

    const {offsetWidth} = firstChild;
    const childStyles = window.getComputedStyle(firstChild);
    const marginWidth = parseInt(childStyles.marginLeft) + parseInt(childStyles.marginRight);
    const displayWidth = Math.floor(containerWidth / offsetWidth) * (offsetWidth + marginWidth);
    const leftOffset = Math.min(marginLeft + displayWidth, 0);

    setMarginLeft(leftOffset);
    setMarginRight(-leftOffset);
  };

  const handleNextClick = () => {
    const {firstChild} = scrollBody.current || {};
    const {offsetWidth: containerWidth, scrollWidth} = scrollContainer.current || {};

    if (
      !firstChild ||
      !(firstChild instanceof HTMLElement) ||
      !firstChild.offsetWidth ||
      !containerWidth ||
      !scrollWidth
    ) {
      return;
    }

    const {offsetWidth} = firstChild;
    const childStyles = window.getComputedStyle(firstChild);
    const marginWidth = parseInt(childStyles.marginLeft) + parseInt(childStyles.marginRight);
    const displayWidth = Math.floor(containerWidth / offsetWidth) * (offsetWidth + marginWidth);

    setEnd((prevEnd) => {
      let newEnd = prevEnd;

      // Calculate margin needed to get to the end on the first click.
      // The 4 adds space to allow shadows to show on the right edge.
      if (!newEnd && marginLeft === 0) {
        newEnd = -scrollWidth + containerWidth - 4;
      }

      const leftOffset = Math.max(marginLeft - displayWidth, newEnd || -Infinity);

      setMarginLeft(leftOffset);
      setMarginRight(-leftOffset);

      return newEnd;
    });
  };

  return (
    <div
      className={classnames('shared-carousel', className)}
      ref={scrollContainer}
      data-testid="shared-carousel"
    >
      <CarouselArrow
        className={classnames('shared-carousel-arrow', 'prev', {visible: isLeftArrowVisible})}
        direction="left"
        onClick={handlePrevClick}
        arrowKey={carouselKey}
        {...arrowProps}
      />
      <div
        className={classnames('shared-carousel-fade', 'fade-left', {
          visible: isLeftArrowVisible && fade,
        })}
      />
      <div className="shared-carousel-display" ref={scrollBody} style={{marginLeft, marginRight}}>
        {children}
      </div>
      <div
        className={classnames('shared-carousel-fade', 'fade-right', {
          visible: isRightArrowVisible && fade,
        })}
      />
      <CarouselArrow
        className={classnames('shared-carousel-arrow', 'next', {visible: isRightArrowVisible})}
        direction="right"
        onClick={handleNextClick}
        arrowKey={carouselKey}
        {...arrowProps}
      />
    </div>
  );
};

export default Carousel;
